import type { AnchorHTMLAttributes } from 'react';
import { useHref, useLocation, useNavigate, useResolvedPath } from 'react-router-dom';

/* In-app links without an href, so the browser never shows the URL in the bottom
   corner on hover (user request). Use these instead of react-router's Link/NavLink.
   Still an <a> (styles keep working), reachable with Tab and opened with Enter;
   Ctrl/⌘/Shift-click and middle-click still open the page in a new tab. */

type Props = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string; state?: unknown; replace?: boolean };

export function Link({ to, state, replace, onClick, onKeyDown, onAuxClick, onMouseDown, ...rest }: Props) {
  const navigate = useNavigate();
  const href = useHref(to);
  const go = () => navigate(to, { state, replace });
  return (
    <a
      role="link"
      tabIndex={0}
      {...rest}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented) return;
        e.preventDefault();
        // target="_blank" (e.g. the install guide from the activation wizard) or Ctrl/⌘/Shift: new tab.
        if (rest.target === '_blank' || e.ctrlKey || e.metaKey || e.shiftKey) window.open(href, '_blank', 'noopener');
        else go();
      }}
      // Stop the middle button starting auto-scroll, then open in a new tab on release.
      onMouseDown={(e) => { onMouseDown?.(e); if (e.button === 1) e.preventDefault(); }}
      onAuxClick={(e) => { onAuxClick?.(e); if (e.button === 1 && !e.defaultPrevented) { e.preventDefault(); window.open(href, '_blank'); } }}
      onKeyDown={(e) => { onKeyDown?.(e); if (e.key === 'Enter' && !e.defaultPrevented) { e.preventDefault(); if (rest.target === '_blank') window.open(href, '_blank', 'noopener'); else go(); } }}
    />
  );
}

/** Link that gets class "active" and aria-current="page" while its page is open. */
export function NavLink({ to, end, className, ...rest }: Props & { end?: boolean }) {
  const { pathname } = useLocation();
  const path = useResolvedPath(to).pathname;
  const active = pathname === path || (!end && (path === '/' || pathname.startsWith(`${path}/`)));
  return <Link to={to} className={[className, active && 'active'].filter(Boolean).join(' ') || undefined} aria-current={active ? 'page' : undefined} {...rest} />;
}
